import classNames from 'classnames';
import { action, computed, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { StringKeyMap } from '../../../adl-gen/common';
import { DbKey, WithDbId } from '../../../adl-gen/common/db';
import * as DB from '../../../adl-gen/totalinteraction/futurefit/db';
import * as FE from '../../../adl-gen/totalinteraction/futurefit/frontend';
import * as R from '../../../adl-gen/totalinteraction/futurefit/requests';
import { withI18n, WithI18nProps } from '../../../internationalization/i18n-hoc';
import { i18next } from '../../../internationalization/i18next';
import { MaybeLoading, Spinner } from '../../../loader/loader';
import { Route } from '../../../routing/app-routes';
import { CloseIcon, HeartBeatIcon } from '../../common/icons';
import { PageLayoutFn } from '../../common/page-layout/page-layout';
import { ScoreSlider } from '../../components/slider/score-slider';
import { Accordion } from '../../widgets/accordion/accordion';
import { Button } from '../../widgets/button/button';
import { Table, TableCell, TableRow } from '../../widgets/table/table';

const styles = require('./self-assessment-question-screen.css');
const fonts = require('../../../ui/common/fonts.css');

/** Props for the component */
export interface SelfAssessmentQuestionScreenProps {
  /** Constructor for the page layout */
  pageLayout: PageLayoutFn;

  /** The quiz   */
  userQuizSummary: MaybeLoading<FE.UserQuizSummary>;

  /** The question to display */
  nextQuestion: MaybeLoading<R.NextQuestionResp>;

  /** Role data */
  workRole: MaybeLoading<FE.WorkRoleListEntry>;

  /** Goto another page */
  navigateTo(route: Route): void;

  /** Heartbeat callback to indicate the page is still alive */
  onHeartbeat(): Promise<void>;

  /** Callback when an answer is selected but not yet submitted */
  onSelect(answer: DB.AnswerResponse, quizQuestionId?: DbKey<DB.QuizQuestion>): Promise<void>;

  /** Callback to answer the question and proceed */
  onNext(answers: R.SelfAssessmentAnswerQuestionReq[]): Promise<void>;

  /** Callback to complete the quiz once done */
  onDone(): Promise<void>;
}

@observer
export class SelfAssessmentQuestionScreen extends React.Component<SelfAssessmentQuestionScreenProps> {
  render() {
    const { nextQuestion, userQuizSummary, workRole } = this.props;
    if (userQuizSummary.kind === 'error') {
      return <div className={styles.errorText}>{userQuizSummary.error}</div>;
    } else if (nextQuestion.kind === 'error') {
      return <div className={styles.errorText}>{nextQuestion.error}</div>;
    } else if (workRole.kind === 'error') {
      return <div className={styles.errorText}>{workRole.error}</div>;
    } else if (userQuizSummary.kind === 'value' && nextQuestion.kind === 'value' && workRole.kind === 'value') {
      return (
        <SelfAssessmentQuestion
          pageLayout={this.props.pageLayout}
          userQuizSummary={userQuizSummary.value}
          nextQuestion={nextQuestion.value.value as R.SelfAssessmentNextQuestionResp}
          workRole={workRole.value}
          navigateTo={this.props.navigateTo}
          onSelect={this.props.onSelect}
          onHeartbeat={this.props.onHeartbeat}
          onNext={this.props.onNext}
          onDone={this.props.onDone}
        />
      );
    } else {
      return <Spinner />;
    }
  }
}

interface AssignedSkillState {
  skill: WithDbId<DB.Skill>;
  actual: number | null;
  quizQuestionId: DbKey<DB.QuizQuestion>;
}

/// ----- [ NOTE: Assumption here is that we'll get reloaded for each competency so no need to key on that ] -----
type AssignedSkillMap = StringKeyMap<DbKey<DB.Skill>, AssignedSkillState>;

/** Props for the component */
export interface SelfAssessmentQuestionProps extends WithI18nProps {
  /** Constructor for the page layout */
  pageLayout: PageLayoutFn;

  /** The quiz   */
  userQuizSummary: FE.UserQuizSummary;

  /** The question to display */
  nextQuestion: R.SelfAssessmentNextQuestionResp;

  /** Role data */
  workRole: FE.WorkRoleListEntry;

  /** Goto another page */
  navigateTo(route: Route): void;

  /** Heartbeat callback to indicate the page is still alive */
  onHeartbeat(): Promise<void>;

  /** Callback when an answer is selected but not yet submitted */
  onSelect(answer: DB.AnswerResponse, quizQuestionId?: DbKey<DB.QuizQuestion>): Promise<void>;

  /** Callback to answer the question and proceed */
  onNext(answers: R.SelfAssessmentAnswerQuestionReq[]): Promise<void>;

  /** Callback to complete the quiz once done */
  onDone(): Promise<void>;
}

/**
 * The Quiz Question Screen screen.
 */
@observer
class SelfAssessmentQuestion0 extends React.Component<SelfAssessmentQuestionProps> {
  @observable answerResponses: AssignedSkillMap = {};

  heartbeatIntervalId: number | null = null;

  /// We have to set answerResponse for an introduction or conclusion question, as there
  /// is no update action available. We can't set this in the render phase.
  constructor(props: Readonly<SelfAssessmentQuestionProps>) {
    super(props);

    if (this.props.userQuizSummary.quizType !== DB.QuizType.selfAssessment) {
      throw new Error('Self-Assessment screen loaded for Quiz not Self-Assessment');
    }

    this.answerResponses = this.loadFieldState();

    /// Intros and Conclusions: Set up response array for 'I have read'/'I accept'-type responses.
    /// Only do this if these answers exist. If the 'slide' has no answers, simply set the field to 'submitted' and move on.
    /// Currently these are not properly supported for self-assessment jobs
    // const nextQuestion = props.nextQuestion
    // if (nextQuestion.kind === "nextQuestion") {
    //   if (nextQuestion.value.answerType.kind === "submitted") {
    //     if (nextQuestion.value.answers.length > 0) {
    //       runInAction(() => {
    //         this.submittedResponses = new Array<boolean>(nextQuestion.value.answers.length)
    //         this.submittedResponses.fill(false)
    //       })
    //     } else {
    //       /// Just set answerResponse
    //       runInAction(() => this.answerResponse = { kind: "submitted" })
    //     }
    //   }
    // }
  }

  componentDidMount() {
    const HEARTBEAT_PERIOD = 30000;
    this.heartbeatIntervalId = window.setInterval(this.props.onHeartbeat, HEARTBEAT_PERIOD);
  }

  /// FIXME: this is deprecated. Can use equivalent useLayoutEffect() hook if it's available. Otherwise use useEffect/useRef
  /** @deprecated */
  componentWillUnmount() {
    if (this.heartbeatIntervalId) {
      window.clearInterval(this.heartbeatIntervalId);
    }
  }

  /// ----- [ Initialize ] -----
  @action
  loadFieldState = (): AssignedSkillMap => {
    let fieldState: AssignedSkillMap = {};

    const { nextQuestion } = this.props;

    /// Set up from list of quiz quesitons in the NextQuestion object
    if (nextQuestion.kind === 'nextQuestion') {
      fieldState = nextQuestion.value.skillQuestions.reduce((acc: AssignedSkillMap, skillQuestion) => {
        const itemState: AssignedSkillState = {
          skill: skillQuestion.v2,
          // initialize to null
          actual: null,
          quizQuestionId: skillQuestion.v1
        };
        acc[skillQuestion.v2.id] = itemState;
        return acc;
      }, {});
    }
    return fieldState;
  };

  @computed
  get answersComplete() {
    return Object.values(this.answerResponses)
      .map(item => item.actual)
      .every(a => a !== null);
  }

  /// ----- [ Log changes ] -----
  @action
  setAllActual = async (value: number) => {
    /// Override all individual skill settings on the Competency
    for (const item of Object.keys(this.answerResponses)) {
      //}).forEach(item => {
      await this.logActual(item)(value);
    }
  };

  /// ----- [ Log changes ] -----
  // Function returns function so @action doesn't work here - use runInAction instead
  //@action
  logActual = (id: DbKey<DB.Skill>) => async (actual: number) => {
    runInAction(() => {
      const itemState = this.answerResponses[id];
      if (itemState) {
        itemState.actual = actual;
      }
    });
    await this.updateSelfAssessmentAnswerResponse();
  };

  @computed
  get avgActualForCompetency() {
    // Calculate current actuals for competency
    const count = Object.keys(this.answerResponses).length;
    const sum =
      count === 0
        ? null
        : Object.values(this.answerResponses).reduce((a: number | null, b) => {
            if (a === null || b.actual === null) {
              return null;
            } else {
              return a + b.actual;
            }
          }, 0);
    return sum === null ? undefined : sum / count;
  }

  CompetencyActualObserver = observer(({ id }) => {
    return (
      <ScoreSlider
        key={id}
        ranges={this.props.workRole.scoreRanges.map(item => item.value)}
        onChange={this.setAllActual}
        value={this.avgActualForCompetency}
      />
    );
  });

  SkillActualObserver = observer(({ id, actual }) => {
    return (
      <ScoreSlider
        key={id}
        ranges={this.props.workRole.scoreRanges.map(item => item.value)}
        onChange={this.logActual(id)}
        value={actual === null ? undefined : actual}
      />
    );
  });

  renderCompetencySet = (question: FE.SelfAssessmentNextQuestion) => {
    const { t } = this.props;
    const { competency } = question;
    return (
      <Accordion
        HeaderIcon={HeartBeatIcon}
        headerTitle={t(competency.value.fullName)}
        // headerSubtitle={headerSubTitle}
        // headerContent={<span>{t(competency.value.definition)}</span>}
        key={competency.id}
        collapsedContent={this.renderCompetencyTable(competency)}
        expandedContent={this.renderSkillTable(this.answerResponses)}
        expanderLabels={['Hide Skills', 'Show Skills']}
      />
    );
  };

  renderCompetencyDefinitionAndSlider = (item: WithDbId<DB.Competency>) => {
    const definition = this.props.t(item.value.definition);
    return (
      <>
        <div className={styles.definition}>
          <span className={fonts.caption}>{definition}</span>
        </div>
        <div className={styles.slider}>
          <this.CompetencyActualObserver id={item.id} />
        </div>
      </>
    );
  };

  renderSkillName = (skill: WithDbId<DB.Skill>) => {
    return <span className={fonts.caption}>{this.props.t(skill.value.name)}</span>;
  };

  renderSkillDefinitionAndSlider = (skill: WithDbId<DB.Skill>, actual: number | null) => {
    const definition = this.props.t(skill.value.definition);
    return (
      <>
        <div className={styles.definition}>
          <span className={fonts.caption}>{definition}</span>
        </div>
        <div className={styles.slider}>
          <this.SkillActualObserver id={skill.id} actual={actual} />
        </div>
      </>
    );
  };
  renderCompetencyTable = (competency: WithDbId<DB.Competency>) => {
    return (
      <Table className={classNames(styles.noBorder, styles.noLeftMargin)}>
        <TableRow>
          <TableCell key={0}>{''}</TableCell>
          <TableCell key={1}>{this.renderCompetencyDefinitionAndSlider(competency)}</TableCell>
        </TableRow>
      </Table>
    );
  };

  renderSkillTable = (competencySkillSet: AssignedSkillMap) => {
    return (
      <Table className={classNames(styles.noBorder, styles.noLeftMargin)}>
        {Object.entries(competencySkillSet).map(([key, value]) => {
          return (
            <TableRow key={key}>
              <TableCell key={0}>{this.renderSkillName(value.skill)}</TableCell>
              <TableCell key={1}>{this.renderSkillDefinitionAndSlider(value.skill, value.actual)}</TableCell>
            </TableRow>
          );
        })}
      </Table>
    );
  };

  /** Renders the component */
  render(): JSX.Element {
    return <this.props.pageLayout>{this.renderContent()}</this.props.pageLayout>;
  }

  renderContent(): JSX.Element {
    switch (this.props.nextQuestion.kind) {
      case 'completed':
        return this.renderCompleted();
      case 'nextQuestion':
        return this.renderQuestion(this.props.nextQuestion.value);
    }
  }

  renderCompleted = (): JSX.Element => {
    const { userQuizSummary } = this.props;
    const completedText = userQuizSummary.lockResults
      ? i18next.t('question.results-locked', "Thank you for completing this assessment. Your results will be available soon.")
      : i18next.t('question.completed', "You've completed this assessment. Continue to see your results.");
    return (
      <div className={styles.container}>
        <div className={styles.margin} />
        <div className={styles.questionContent}>
          <p>{completedText}</p>
          <br />
          <Button onClick={this.props.onDone} variant="round">
            {i18next.t('question.continue', 'Continue')}
          </Button>
        </div>
        <div className={styles.margin} />
      </div>
    );
  };

  returnToAssessmentsScreen(): void {
    return this.props.navigateTo({
      route: 'my-assessments'
    });
  }

  /// 'Question' - one per competency - will not have a 'back' button and
  /// therefore we can assume that the actual values on entry to this routine
  /// are all empty. Also this is progressed as per a quiz and each next
  /// or save-and-close or exit navigation is an all-or-nothing action.
  renderQuestion(question: FE.SelfAssessmentNextQuestion): JSX.Element {
    /// default
    /// For display *only*
    const questionNumber = question.questionNumber;
    const totalQuestions = question.totalQuestions;
    const title = i18next.t('question.heading', {
      questionNumber,
      totalQuestions,
      defaultValue: `${questionNumber} of ${totalQuestions}`
    });

    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.exit}>
            <CloseIcon size={'xsmall'} onClick={async () => this.returnToAssessmentsScreen()} />
          </div>
          <div className={styles.title}>{title}</div>
          <div className={styles.question}>{this.renderCompetencySet(question)}</div>
          <div className={styles.buttons}>
            <Button
              // tslint:disable-next-line: no-async-without-await
              onClick={async () => this.returnToAssessmentsScreen()}
              variant={'tertiary'}
              className={styles.button}
            >
              {i18next.t('question.save-close', 'Save and Close')}
            </Button>
            <Button onClick={this.onNextQuestion} disabled={!this.answersComplete} className={styles.button}>
              {question.questionNumber === question.totalQuestions
                ? i18next.t('question.finish', 'Finish')
                : i18next.t('question.next', 'Next')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // submitAnswer = async (actual: number, quizQuestionId: DbKey<DB.QuizQuestion>) => {
  //   await this.props.onNext({
  //     userQuizId: this.props.userQuizSummary.userQuizId,
  //     quizQuestionId,
  //     answerResponse: { actual }
  //   })
  // }

  onNextQuestion = async (): Promise<void> => {
    if (this.answersComplete && this.props.nextQuestion.kind === 'nextQuestion') {
      const answers: R.SelfAssessmentAnswerQuestionReq[] = Object.values(this.answerResponses)
        .filter(answer => answer.actual !== null)
        .reduce((acc: R.SelfAssessmentAnswerQuestionReq[], answer) => {
          if (answer.actual !== null) {
            acc.push({
              userQuizId: this.props.userQuizSummary.userQuizId,
              quizQuestionId: answer.quizQuestionId,
              answerResponse: { actual: answer.actual }
            });
          }
          return acc;
        }, []);
      await this.props.onNext(answers);
    }
  };

  updateAnswerResponse = async (value: number, quizQuestionId: DbKey<DB.QuizQuestion>) => {
    await this.props.onSelect(
      {
        kind: 'singleResponse',
        value
      },
      quizQuestionId
    );
  };

  updateSelfAssessmentAnswerResponse = async () => {
    /// If every skill has an actual set, set to 'submitted' and enable 'Next' button.
    if (this.answersComplete) {
      /// Make sure this runs in SERIES not in PARALLEL
      /// forEach will still run asynchronously
      // Object.values(this.answerResponses).reduce((promise: Promise<void>, item) => {
      //   if (item.actual !== null) {
      //     const value = item.actual
      //     promise.then(() => this.updateAnswerResponse(value, item.quizQuestionId))
      //   }
      //   return promise
      // }, Promise.resolve())
      for (const item of Object.values(this.answerResponses)) {
        //}.forEach(async item => {
        if (item.actual !== null) {
          await this.updateAnswerResponse(
            // {
            // kind: "singleResponse",
            // value: item.actual
            // },
            item.actual,
            item.quizQuestionId
          );
        }
      }
    }
  };

  // _updateAnswerResponse = async (answerResponse: DB.AnswerResponse, quizQuestionId?: DbKey<DB.QuizQuestion>) => {
  //   await this.props.onSelect(answerResponse, quizQuestionId)
  // }
}

export const SelfAssessmentQuestion = withI18n(SelfAssessmentQuestion0);



// WEBPACK FOOTER //
// ./src/ui/screens/self-assessment-question-screen/self-assessment-question-screen.tsx